import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb, { getCategoryLabel } from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { getMoviesByCategory, categories } from '@/mocks/movies';

export default function Category() {
  const { category = 'all' } = useParams<{ category: string }>();
  const [activeCat, setActiveCat] = useState(category);

  useEffect(() => {
    setActiveCat(category);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [category]);

  const movies = getMoviesByCategory(activeCat);
  const catLabel = getCategoryLabel(activeCat);

  const heroBgUrl = 'https://readdy.ai/api/search-image?query=dark%20elegant%20cinema%20hallway%20with%20warm%20golden%20wall%20sconces%2C%20classic%20movie%20posters%20on%20walls%2C%20rich%20red%20carpet%2C%20atmospheric%20lighting%2C%20luxurious%20theater%20interior%2C%20cinematic%20mood%2C%20soft%20shadows&width=1920&height=500&seq=yaya-cat-hero&orientation=landscape';

  return (
    <>
      <Navbar />

      <main className="pt-16">
        {/* Category Hero */}
        <section className="relative h-[280px] md:h-[350px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={heroBgUrl}
              alt={`${catLabel}分类`}
              className="w-full h-full object-cover object-center"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/70"></div>
          <div className="relative z-10 text-center px-4">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-white mb-3">
              {catLabel}
            </h1>
            <p className="text-sm md:text-base text-white/70 max-w-lg mx-auto">
              丫丫影院{catLabel}频道 — 最新{catLabel}高清免费在线观看，播放不缓冲
            </p>
          </div>
        </section>

        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-6 bg-background-50 border-b border-background-200">
          <Breadcrumb
            items={[
              { label: catLabel, href: activeCat === 'all' ? '/' : `/category/${activeCat}` },
            ]}
          />
        </div>

        {/* Category Tabs */}
        <div className="py-6 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.key}
                  to={cat.key === 'all' ? '/' : `/category/${cat.key}`}
                  className={`flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeCat === cat.key
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 hover:text-foreground-950 hover:bg-background-200'
                  }`}
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                >
                  <i className={`${cat.icon} text-base`}></i>
                  {cat.label}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Movie Grid */}
        <section className="py-8 md:py-12 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                共 {movies.length} 部{catLabel}
              </h2>
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500">排序：</span>
                <select className="px-3 py-1.5 text-sm bg-background-100 border border-background-200 rounded-lg text-foreground-700 focus:outline-none focus:border-primary-400 cursor-pointer">
                  <option>最新更新</option>
                  <option>评分最高</option>
                  <option>最受欢迎</option>
                </select>
              </div>
            </div>
            <div
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5"
              data-product-shop
            >
              {movies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
            {movies.length === 0 && (
              <div className="text-center py-16 text-foreground-500">
                <i className="ri-inbox-line text-4xl block mb-3"></i>
                <p>该分类暂无内容</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `丫丫影院 - ${catLabel}`,
            description: `丫丫影院${catLabel}频道提供最新${catLabel}高清免费在线观看，播放不缓冲。`,
            url: `https://yaya.tv/category/${activeCat}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '丫丫影院',
              url: 'https://yaya.tv/',
            },
          }),
        }}
      />
    </>
  );
}